import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { useCallback, useContext, useState } from 'react';

import { SESSION_EXPIRED } from 'constants/common';
import { useErrorHandler } from 'hooks/useErrorHandler';
import { UserContext } from '../../context/UserContext';

interface IAxiosState {
    response: AxiosResponse | undefined;
    error: string | unknown;
    loading: boolean;
}

export interface IRequestHandlers {
    onSuccess?: (_response: AxiosResponse) => void;
    onError?: (_error: unknown) => void;
}

export const useAxios = () => {
    const [state, setState] = useState<IAxiosState>({
        response: undefined,
        error: '',
        loading: false
    });
    const errorHandler = useErrorHandler();
    const { session } = useContext(UserContext);

    const request = useCallback(
        async (config: AxiosRequestConfig, handlers?: IRequestHandlers) => {
            setState(state => ({
                ...state,
                loading: true,
                error: ''
            }));

            try {
                const response = await axios({
                    ...config,
                    headers: {
                        Authorization: session.jwt,
                        ...config.headers
                    }
                });
                setState(state => ({
                    ...state,
                    response,
                    error: '',
                    loading: false
                }));
                handlers && handlers.onSuccess && handlers.onSuccess(response);
                return response;
            } catch (error) {
                const errorMessage = await errorHandler(error as AxiosError);
                setState(state => ({ ...state, error: errorMessage, loading: false }));
                if (errorMessage === SESSION_EXPIRED) return;
                handlers && handlers.onError && handlers.onError(error);
            }
        },
        [setState, session]
    );

    return { ...state, request };
};
